import {
  Box,
  Typography,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  useMediaQuery,
  Drawer,
  ListItem,
  List,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowUpward,
  Delete,
  Feedback,
  Menu,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import React, { useEffect, useState, useRef } from "react";
import colors from "../../../Constants/colors";
import {
  ChatBoxMessagesStyles,
  textfieldLayout,
  textfieldLayoutNormal,
} from "./DeployedBotsStyles";
import { useDispatch, useSelector } from "react-redux";
import {
  createNewMemorySession,
  deleteSession,
  getSession,
  getUserBotMemory,
} from "../../../features/auth/authSlice";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formattedDate } from "../../../utils/utilityFunctions";

const ChatBot = ({ selectedBot, handleBackClick }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [generatingMessage, setGeneratingMessage] = useState(false);

  const [deployedVersions, setDeployedVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState("");

  const messageEndRef = useRef(null);
  const dispatch = useDispatch();
  const { user, botMemory, isSuccess } = useSelector((state) => state.auth);
  const { organization } = useSelector((state) => state.organization);

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const isLargeScreen = useMediaQuery("(min-width: 1024px)"); // Adjust breakpoint as needed
  const [previousSessions, setPreviousSessions] = useState({});
  const [sessionMessages, setSessionMessages] = useState({});
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);

  const user_id = user?.user?._id["$oid"];
  const bot_id = selectedBot.id;
  const orgId = organization?._id["$oid"];

  // Function to scroll to the bottom of the messages
  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Scroll to the bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch bot memory if the selected bot has memory
  useEffect(() => {
    if (selectedBot?.have_memory) {
      const data = { user_id, bot_id };
      setChatHistoryLoading(true);
      setSnackbarMessage("Getting Chat History...");
      setSnackbarOpen(true);

      dispatch(getUserBotMemory(data))
        .unwrap()
        .catch(() => {
          // Handle rejection specifically
          setChatHistoryLoading(false);
          setSnackbarMessage("No chat history found for this bot.");
          setSnackbarOpen(true);
        })
        .finally(() => {
          setChatHistoryLoading(false);
          setSnackbarOpen(false);
        });
    } else {
      setChatHistoryLoading(false); // Ensure loading is stopped if no memory
    }
  }, []);

  useEffect(() => {
    if (isSuccess && botMemory.length > 0) {
      const memory = botMemory.find(
        (memory) => memory.bot_id === selectedBot.id
      );
      if (memory && memory.memory && memory.memory.sessions) {
        // Convert sessions object into a structured format
        const formattedSessions = Object.entries(memory.memory.sessions).reduce(
          (acc, [query, sessionData]) => {
            const sessionId = sessionData.id?.$oid || sessionData.id; // Extract the session ID properly
            if (!sessionId) return acc; // Skip if sessionId is missing

            acc.push({
              id: sessionId,
              title: query,
              last_updated:
                sessionData.last_updated?.$date || sessionData.last_updated, // Extract timestamp correctly
            });

            return acc;
          },
          []
        );
        // Sort sessions by last_updated in descending order (latest first)
        formattedSessions.sort(
          (a, b) => new Date(b.last_updated) - new Date(a.last_updated)
        );

        // Convert sorted array back into an object
        const sortedSessions = formattedSessions.reduce((acc, session) => {
          acc[session.id] = {
            title: session.title,
            last_updated: session.last_updated,
          };
          return acc;
        }, {});

        setPreviousSessions(sortedSessions);
      } else if (memory?.memory === null) {
        setSnackbarMessage("No chat history found for this bot.");
        setSnackbarOpen(true);
      }
      setChatHistoryLoading(false);
    }
  }, [botMemory, selectedBot]);

  // 2) whenever selectedBot.default_flow changes, rebuild + pick latest
  useEffect(() => {
    const flow = selectedBot.default_flow || {};
    // convert { id1: {…}, id2: {…} } → [ {id, name, retrieval, generation, updated_at}, … ]
    const versions = Object.entries(flow).map(([id, meta]) => ({
      id,
      name: meta.name,
      retrieval: meta.retrieval,
      generation: meta.generation,
      updated_at: meta.updated_at,
    }));
    // sort newest first
    versions.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    setDeployedVersions(versions);
    if (versions.length > 0) {
      setSelectedVersionId(versions[0].id);
    }
  }, [selectedBot.default_flow]);

  const handleSendMessage = async () => {
    if (newMessage.trim() === "") return;

    // Add the user's message to the conversation
    const userMessage = { sender: "user", text: newMessage };
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    if (selectedSessionId) {
      setSessionMessages((prev) => ({
        ...prev,
        [selectedSessionId]: [...(prev[selectedSessionId] || []), userMessage],
      }));
    }

    setNewMessage("");
    setIsLoading(true);

    // Add a temporary placeholder message for the bot's "Generating..." response
    setMessages((prevMessages) => [
      ...prevMessages,
      { sender: "bot", text: "Generating..." }, // Temporary bot response
    ]);

    const token = JSON.parse(localStorage.getItem("token"));
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    let sessionId = selectedSessionId;

    if (!sessionId) {
      const sessionTitle = newMessage.split(" ").slice(0, 5).join(" ");
      try {
        const res = await dispatch(
          createNewMemorySession({ user_id, bot_id, sessionTitle })
        );

        if (res.error) {
          console.error("Error creating session:", res.error);
          setSnackbarMessage("Session creation failed.");
          setSnackbarOpen(true);
          return;
        }

        const sessionData = res.payload;
        if (!sessionData?.session_id) {
          setSnackbarMessage("Failed to create a new session. Try again.");
          setSnackbarOpen(true);
          return;
        }

        sessionId = sessionData.session_id;

        setPreviousSessions((prev) => ({
          ...prev,
          [sessionId]: {
            title: sessionTitle,
            last_updated: new Date().toISOString(),
          },
        }));

        setSelectedSessionId(sessionId);
        setSessionMessages((prev) => ({
          ...prev,
          [sessionId]: [userMessage], // Initialize session with the user message
        }));
      } catch (error) {
        setSnackbarMessage("Error creating session.");
        setSnackbarOpen(true);
        return;
      }
    } else {
      // Update last modified timestamp in `previousSessions`
      setPreviousSessions((prev) => ({
        ...prev,
        [sessionId]: {
          ...prev[sessionId],
          last_modified: new Date().toISOString(),
        },
      }));
    }

    if (!sessionId) {
      setSnackbarMessage("Session ID not found. Please start a new session.");
      setSnackbarOpen(true);
      return;
    }

    const version = deployedVersions.find((v) => v.id === selectedVersionId);

    const data = {
      session_id: sessionId,
      flow: {
        Retrieval: version.retrieval,
        Generation: version.generation,
      },
      query: newMessage,
    };

    try {
      const response = await fetch(
        `/api/eval_bot/chatend2end/retrieval/${orgId}/${user_id}/${bot_id}`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // // Clear the bot's placeholder message for typewriter effect
      // setMessages((prevMessages) => {
      //   if (prevMessages.length === 0) return prevMessages; // Prevent updating an empty array

      //   const updatedMessages = [...prevMessages];

      //   // Ensure last message exists before modifying it
      //   if (updatedMessages[updatedMessages.length - 1]) {
      //     updatedMessages[updatedMessages.length - 1] = {
      //       ...updatedMessages[updatedMessages.length - 1], // Keep other properties
      //       text: "", // Clear placeholder
      //     };
      //   }

      //   return updatedMessages;
      // });

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let botMessage = "";

      // Read the stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the chunk and append it to the bot's message
        const chunk = decoder.decode(value, { stream: true });
        botMessage += chunk;

        if (!version.generation || version.generation.length === 0) {
          const { valid, data } = validateAndFixJson(botMessage);
          if (valid) {
            // Extract the answer field
            botMessage = data.answer;
          }
        }

        // Update the bot's message in real-time with Markdown rendering
        setMessages((prevMessages) => {
          if (prevMessages.length === 0) return prevMessages; // Prevent updating an empty array

          const updatedMessages = [...prevMessages];

          // Modify the last bot message instead of overwriting the array
          updatedMessages[updatedMessages.length - 1] = {
            ...updatedMessages[updatedMessages.length - 1],
            text: (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {botMessage}
              </ReactMarkdown>
            ),
          };

          return updatedMessages;
        });
      }

      setSessionMessages((prev) => {
        const sessionKey = sessionId; // this is either new or existing session
        const existingMessages = prev?.[sessionKey] || [];
        return {
          ...prev,
          [sessionKey]: [
            ...existingMessages,
            { sender: "bot", text: botMessage },
          ],
        };
      });

      setGeneratingMessage(false); // Typing complete
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", text: "Error fetching response" },
      ]);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const validateAndFixJson = (jsonString) => {
    try {
      // Escape unescaped double quotes inside string values
      jsonString.replace(/(?<!\\)"/g, '\\"');
      // Replace single quotes with double quotes
      jsonString = jsonString.replace(
        "'answer': 'Document",
        '"answer": "Document'
      );
      jsonString = jsonString.replace("'queries':", '"queries":');
      // Validate JSON
      return { valid: true, data: JSON.parse(jsonString) };
    } catch (error) {
      // Attempt to fix common issues
      console.warn("Invalid JSON detected, attempting to fix:", error.message);

      // Check if the string might be incomplete and append closing braces
      const openBraces = (jsonString.match(/{/g) || []).length;
      const closeBraces = (jsonString.match(/}/g) || []).length;

      if (openBraces > closeBraces) {
        jsonString += "}".repeat(openBraces - closeBraces); // Add missing closing braces
      }

      // Attempt to validate again
      try {
        return { valid: true, data: JSON.parse(jsonString) };
      } catch (finalError) {
        console.error("Failed to fix JSON:", finalError.message);
        return { valid: false, data: null };
      }
    }
  };

  const handleNewSession = () => {
    setSelectedSessionId(null);
    setMessages([]);
  };

  const handleSessionClick = async (sessionId) => {
    setSelectedSessionId(sessionId);
    setMessages([]); // Clear previous messages

    // Check if messages are already stored for this session
    if (sessionMessages[sessionId]) {
      setMessages(sessionMessages[sessionId]); // Use cached messages
      return;
    }

    // setChatHistoryLoading(true);

    dispatch(getSession(sessionId))
      .unwrap()
      .then((data) => {
        const formattedMessages = data.session.messages
          .map((msg) => [
            { sender: "user", text: msg.USER },
            {
              sender: "bot",
              text: msg.AI || "AI successfully Retrieved Documents.",
              likes: msg.likes || false, // Ensure likes are included
              dislikes: msg.dislikes || false, // Ensure dislikes are included
              feedbacks: msg.feedback || [], // Store any previous feedback
            },
          ])
          .flat(); // Flatten the array to avoid nesting

        setMessages(formattedMessages);
        setSessionMessages((prev) => ({
          ...prev,
          [sessionId]: formattedMessages,
        }));
      })
      .catch(() => {
        setSnackbarMessage("Failed to load session.");
        setSnackbarOpen(true);
      });
    // .finally(() => setChatHistoryLoading(false));
  };

  const handleDeleteSession = async (sessionId) => {
    dispatch(deleteSession(sessionId))
      .unwrap()
      .then(() => {
        setPreviousSessions((prev) => {
          const updatedSessions = { ...prev };
          delete updatedSessions[sessionId]; // Remove session from list
          return updatedSessions;
        });

        setSessionMessages((prev) => {
          const updatedMessages = { ...prev };
          delete updatedMessages[sessionId]; // Remove session messages
          return updatedMessages;
        });

        // If the deleted session was active, reset selected session
        if (selectedSessionId === sessionId) {
          setSelectedSessionId(null);
          setMessages([]); // Clear messages
        }

        setSnackbarMessage("Session deleted.");
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarMessage("Failed to delete session.");
        setSnackbarOpen(true);
      });
  };

  const handleVote = async (index, voteType) => {
    const message = messages[index];
    if (!message || message.sender !== "bot") return;

    // Determine likes/dislikes toggle behavior
    const newLikes = voteType === 1 ? !message.likes : false;
    const newDislikes = voteType === -1 ? !message.dislikes : false;

    // Calculate message_id (assuming alternate user/bot messages)
    const messageId = Math.floor(index / 2);
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await fetch(
        `/api/user/session/feedback/${user_id}/${selectedSessionId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message_id: messageId,
            likes: newLikes,
            dislikes: newDislikes,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update feedback");

      // Update UI with new vote status
      setMessages((prevMessages) =>
        prevMessages
          ? prevMessages.map((msg, i) =>
              i === index && msg.sender === "bot"
                ? { ...msg, likes: newLikes, dislikes: newDislikes }
                : msg
            )
          : []
      );

      // Update session messages
      if (selectedSessionId) {
        setSessionMessages((prev) => ({
          ...prev,
          [selectedSessionId]: prev
            ? prev[selectedSessionId].map((msg, i) =>
                i === index && msg.sender === "bot"
                  ? { ...msg, likes: newLikes, dislikes: newDislikes }
                  : msg
              )
            : {},
        }));
      }
    } catch (error) {
      console.error("Error sending vote:", error);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackMessage.trim()) return;

    const messageId = Math.floor(selectedMessageId / 2);
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await fetch(
        `/api/user/session/feedback/${user_id}/${selectedSessionId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message_id: messageId,
            feedback: feedbackMessage.trim(),
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to submit feedback");

      // Update messages state with new feedback
      setMessages((prev) =>
        prev
          ? prev.map((msg, i) =>
              i === selectedMessageId && msg.sender === "bot"
                ? {
                    ...msg,
                    feedbacks: [...(msg.feedbacks || []), feedbackMessage],
                  }
                : msg
            )
          : []
      );

      // Update session messages
      if (selectedSessionId) {
        setSessionMessages((prev) => ({
          ...prev,
          [selectedSessionId]: Array.isArray(prev?.[selectedSessionId])
            ? prev[selectedSessionId].map((msg, i) =>
                i === selectedMessageId && msg.sender === "bot"
                  ? {
                      ...msg,
                      feedbacks: [...(msg.feedbacks || []), feedbackMessage],
                    }
                  : msg
              )
            : [],
        }));
      }

      setFeedbackMessage("");
      setFeedbackDialogOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };

  // Rendering Sidebar Drawer and Box
  const renderSessions = () => (
    <List>
      {Object.entries(previousSessions || {})
        .filter(([, session]) => session?.title)
        .map(([sessionKey, session]) => (
          <ListItem
            key={sessionKey} // Use sessionKey instead of session.id
            onClick={() =>
              isLoading || chatHistoryLoading || generatingMessage
                ? null
                : handleSessionClick(sessionKey)
            }
            sx={{
              backgroundColor:
                selectedSessionId === sessionKey
                  ? colors.primaryBlue
                  : "transparent",
              borderRadius: "8px",
              border: `1px solid ${colors.extremeWhite}`,
              mb: 1,
              "&:hover": { backgroundColor: colors.tertiaryBlue },
            }}
          >
            <Typography
              sx={{ color: colors.extremeWhite, fontSize: "0.8rem", flex: 1 }}
            >
              {typeof session?.title === "string"
                ? session.title.split(" ").slice(0, 5).join(" ")
                : "Untitled Session"}
            </Typography>

            <IconButton
              onClick={() => handleDeleteSession(sessionKey)}
              disabled={isLoading || chatHistoryLoading || generatingMessage}
            >
              <Delete sx={{ color: "red" }} />
            </IconButton>
          </ListItem>
        ))}
    </List>
  );

  console.log("deployedVersions", deployedVersions);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        my: 2,
        height: "100vh",
        boxShadow: `0px 0px 70px ${colors.secondaryBlue}`,
      }}
    >
      {/* chat area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "100%",
          backgroundColor: colors.extremeBlue,

          py: 2,
          px: 4,
          overflowY: "auto",

          borderRadius: "8px",
        }}
      >
        {/* top bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: `1px solid ${colors.extremeWhite}`,
            pb: 2,
            my: 1,
          }}
        >
          <IconButton
            onClick={handleBackClick}
            sx={{
              color: colors.extremeWhite,
              backgroundColor: colors.primaryBlue,
              "&:hover": {
                backgroundColor: colors.tertiaryBlue,
              },
              boxShadow: `0px 4px 6px ${colors.secondaryBlue}`,
              marginRight: 2,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              flex: 1,
              textAlign: "center",
              color: colors.extremeWhite,
            }}
          >
            Chat with {selectedBot.name}
          </Typography>
          {!isLargeScreen && (
            <IconButton
              onClick={toggleSidebar}
              sx={{
                color: colors.extremeWhite,
                backgroundColor: colors.primaryBlue,
                "&:hover": { backgroundColor: colors.tertiaryBlue },
                boxShadow: `0px 4px 6px ${colors.secondaryBlue}`,
                marginRight: 2,
              }}
            >
              <Menu />
            </IconButton>
          )}
        </Box>
        {/* chat msgs */}
        <Box sx={ChatBoxMessagesStyles}>
          {(messages ?? []).map((message, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent:
                  message.sender === "user" ? "flex-end" : "flex-start",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  maxWidth: "70%",
                  padding: "10px 15px",
                  borderRadius: "15px",
                  backgroundColor:
                    message.sender === "user"
                      ? colors.primaryBlue
                      : colors.textFieldBackground,
                  color: colors.extremeWhite,
                  boxShadow: `0px 4px 6px ${colors.secondaryBlue}`,
                  // "&::after": message.text === "Generating" && {
                  //   content: "'...'",
                  //   display: "inline-block",
                  //   animation: "dots 1.5s steps(1, end) infinite",
                  // },
                  // "@keyframes dot-blink": message.text === "Generating" && {
                  //   "0%": { content: '"."' }, // 1 dot
                  //   "33%": { content: '".."' }, // 2 dots
                  //   "66%": { content: '"..."' }, // 3 dots
                  //   "100%": { content: '""' }, // No dots (restarts)
                  // },
                }}
              >
                {/* Render the message content */}
                {message.sender !== "user" &&
                !generatingMessage &&
                typeof message.text === "string" ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.text}
                  </ReactMarkdown>
                ) : (
                  message.text
                )}
                {/* Display vote only for bot messages */}
                {message.sender === "bot" &&
                  !generatingMessage &&
                  !isLoading &&
                  message.text && (
                    <Box sx={{ display: "flex", mt: 1 }}>
                      <IconButton
                        sx={{ color: message.likes ? "green" : "white" }}
                        onClick={() => handleVote(index, 1)}
                      >
                        <ThumbUp sx={{ fontSize: 16 }} />
                      </IconButton>

                      <IconButton
                        sx={{ color: message.dislikes ? "red" : "white" }}
                        onClick={() => handleVote(index, -1)}
                      >
                        <ThumbDown sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        sx={{ color: "white" }}
                        onClick={() => {
                          setSelectedMessageId(index);
                          setFeedbackDialogOpen(true);
                        }}
                      >
                        <Feedback sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
              </Box>
            </Box>
          ))}
          <div ref={messageEndRef} />
        </Box>
        {/* textfield */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            variant="outlined"
            placeholder="Type your message..."
            fullWidth
            sx={textfieldLayout}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading || chatHistoryLoading || generatingMessage}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1 }}>
                  <FormControl variant="standard" sx={{ minWidth: 140 }}>
                    <Select
                      value={selectedVersionId}
                      onChange={(e) => setSelectedVersionId(e.target.value)}
                      variant="standard"
                      disableUnderline
                      sx={{
                        color: colors.extremeWhite,
                        "& .MuiSvgIcon-root": {
                          color: colors.extremeWhite,
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: colors.extremeBlue,
                          },
                        },
                      }}
                    >
                      {deployedVersions.map((v) => (
                        <MenuItem
                          key={v.id}
                          value={v.id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: colors.extremeWhite }}
                          >
                            {v.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.lightGray }}
                          >
                            {formattedDate(v.updated_at)}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            sx={{
              color: colors.extremeWhite,
              backgroundColor: colors.primaryBlue,
              "&:hover": {
                backgroundColor: colors.tertiaryBlue,
              },
              "&.Mui-disabled": {
                backgroundColor: colors.darkGray,
                color: colors.lightGray,
              },
            }}
            onClick={handleSendMessage}
            disabled={isLoading || chatHistoryLoading || generatingMessage}
          >
            <ArrowUpward />
          </IconButton>
        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="info"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
      {/* Right Sidebar */}
      {isLargeScreen ? (
        <Box
          sx={{
            width: "300px",
            backgroundColor: colors.extremeBlue,
            borderLeft: `2px solid ${colors.extremeWhite}`,
            color: colors.extremeWhite,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            overflowY: "auto",
            p: 2,
            borderRadius: "0 8px 8px 0",
            mt: 1,
          }}
        >
          <Box>
            <Button
              variant="contained"
              sx={{
                width: "100%",
                backgroundColor: colors.primaryBlue,
                color: colors.extremeWhite,
                textTransform: "none",
                borderRadius: "8px",
                mb: 2,
                "&:hover": {
                  backgroundColor: colors.tertiaryBlue,
                },
              }}
              onClick={handleNewSession}
              disabled={isLoading || chatHistoryLoading || generatingMessage}
            >
              + New Session
            </Button>

            {/* List of Sessions */}
            <Typography
              variant="body1"
              sx={{
                mb: 1,
                pt: 1,
                color: colors.extremeWhite,
                fontWeight: "bold",
                borderTop: `1px solid ${colors.extremeWhite}`,
              }}
            >
              Previous Sessions
            </Typography>
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: "calc(100vh - 150px)",
                WebkitOverflowScrolling: "touch", // For smooth scrolling on iOS
                scrollbarWidth: "thin", // For Firefox
                scrollbarColor: `${colors.secondaryBlue} ${colors.extremeBlack}`, // Custom scrollbar thumb and track colors
                "&::-webkit-scrollbar": {
                  width: "8px", // Custom scrollbar width
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: colors.secondaryBlue, // Custom scrollbar thumb color
                  borderRadius: "10px", // Rounding scrollbar edges
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: colors.extremeBlack, // Track background color
                  borderRadius: "10px", // Round track edges for consistency
                },
                pr: 1,
              }}
            >
              {chatHistoryLoading ? (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    pt: 1,
                    color: colors.extremeWhite,
                    fontWeight: "bold",
                  }}
                >
                  Loading chat history...
                </Typography>
              ) : Object.keys(previousSessions).length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    pt: 1,
                    color: colors.extremeWhite,
                    fontWeight: "bold",
                  }}
                >
                  No previous sessions available.
                </Typography>
              ) : (
                renderSessions()
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <Drawer
          anchor="right"
          open={isSidebarOpen}
          onClose={toggleSidebar}
          sx={{
            "& .MuiDrawer-paper": {
              width: "300px",
              backgroundColor: colors.extremeBlue,
              borderLeft: `2px solid ${colors.extremeWhite}`,
              color: colors.extremeWhite,
              p: 2,
              height: "100vh",
            },
          }}
        >
          <Box>
            <Button
              variant="contained"
              sx={{
                width: "100%",
                backgroundColor: colors.primaryBlue,
                color: colors.extremeWhite,
                textTransform: "none",
                borderRadius: "8px",
                mb: 2,
                "&:hover": {
                  backgroundColor: colors.tertiaryBlue,
                },
              }}
              onClick={handleNewSession}
            >
              + New Session
            </Button>

            {/* List of Sessions */}
            <Typography
              variant="body1"
              sx={{
                mb: 1,
                pt: 1,
                color: colors.extremeWhite,
                fontWeight: "bold",
                borderTop: `1px solid ${colors.extremeWhite}`,
              }}
            >
              Previous Sessions
            </Typography>
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: "calc(100vh - 150px)",
                WebkitOverflowScrolling: "touch", // For smooth scrolling on iOS
                scrollbarWidth: "thin", // For Firefox
                scrollbarColor: `${colors.secondaryBlue} ${colors.extremeBlack}`, // Custom scrollbar thumb and track colors
                "&::-webkit-scrollbar": {
                  width: "8px", // Custom scrollbar width
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: colors.secondaryBlue, // Custom scrollbar thumb color
                  borderRadius: "10px", // Rounding scrollbar edges
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: colors.extremeBlack, // Track background color
                  borderRadius: "10px", // Round track edges for consistency
                },
                pr: 1,
              }}
            >
              {chatHistoryLoading ? (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    pt: 1,
                    color: colors.extremeWhite,
                    fontWeight: "bold",
                  }}
                >
                  Loading chat history...
                </Typography>
              ) : Object.keys(previousSessions).length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    pt: 1,
                    color: colors.extremeWhite,
                    fontWeight: "bold",
                  }}
                >
                  No previous sessions available.
                </Typography>
              ) : (
                renderSessions()
              )}
            </Box>
          </Box>
        </Drawer>
      )}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth={"md"}
        sx={{ width: "100%" }}
        fullWidth
      >
        <DialogTitle
          sx={{ bgcolor: colors.extremeBlue, color: colors.extremeWhite }}
        >
          Provide Feedback
        </DialogTitle>
        <DialogContent
          sx={{ bgcolor: colors.extremeBlue, color: colors.extremeWhite }}
        >
          {/* Display Previous Feedback */}
          {selectedMessageId !== null &&
            messages[selectedMessageId]?.feedbacks &&
            messages[selectedMessageId].feedbacks.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: colors.extremeWhite }}
                >
                  Previous Feedback:
                </Typography>
                {messages[selectedMessageId].feedbacks.map((fb, i) => (
                  <Typography
                    key={i}
                    sx={{ fontSize: "0.9rem", color: "white" }}
                  >
                    - {fb}
                  </Typography>
                ))}
              </Box>
            )}

          {/* Feedback Input Field */}
          <TextField
            fullWidth
            multiline
            rows={3}
            value={feedbackMessage}
            sx={textfieldLayoutNormal}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            placeholder="Write your feedback here..."
          />
        </DialogContent>

        <DialogActions
          sx={{ bgcolor: colors.extremeBlue, color: colors.extremeWhite }}
        >
          <Button
            sx={{ color: colors.extremeWhite }}
            onClick={() => setFeedbackDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{ color: colors.extremeWhite }}
            onClick={handleFeedbackSubmit}
            variant="contained"
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatBot;
